// Matches the names in an export to people on Rhema, and turns parsed messages into what is sent.

import type { ImportedMessage } from "../content";
import { toBase64 } from "../media";
import { type CountryCode, normalisePhone } from "../phone";
import type { WhatsAppMessage } from "./whatsapp";

/** Someone the person could match a sender to, such as a contact. */
export type Candidate = {
  id: string;
  display_name: string;
  /** The person's number in E.164, when Rhema shares it with the importer. */
  phone?: string | null;
};

export type SenderMatch = {
  /** The Rhema person, or null to keep the sender as a name only. */
  userId: string | null;
  /** Why it was suggested. "you" means the name or number is the importer's own. */
  reason: "name" | "phone" | "email" | "you" | null;
};

function fold(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[‎‏‪-‮⁦-⁩~]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

const MARKS = /[‎‏‪-‮⁦-⁩ ]/g;

/**
 * A sender WhatsApp showed as a phone number, such as "+44 7700 900123", in E.164. Null for a
 * name. WhatsApp shows numbers for people the exporter had not saved, with the country code.
 */
export function senderPhone(sender: string, region?: CountryCode): string | null {
  const s = sender.replace(MARKS, "").trim();
  if (!/^\+?[\d\s\-().]{7,20}$/.test(s)) return null;
  return normalisePhone(s, region);
}

/** A sender WhatsApp showed as an email address, in lower case. Null otherwise. */
export function senderEmail(sender: string): string | null {
  const s = sender.replace(MARKS, "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? s : null;
}

export type MatchOptions = {
  /** Whether finding people by phone number is on for this server. */
  phone: boolean;
  /** Region for numbers written without a country code. */
  region?: CountryCode;
  /** People the server found for a sender's number or email address, by sender. */
  found?: Record<string, { userId: string; reason: "phone" | "email" }>;
};

/**
 * Suggests a Rhema person for each sender. The importer's own name or number comes first, then a
 * saved name that matches exactly. When phone numbers are on, a contact's number comes next. Last
 * comes whoever the server found for the sender's number or email address. A name that fits more
 * than one person is left for the importer to choose.
 */
export function suggestMatches(
  senders: string[],
  people: Candidate[],
  me: Candidate,
  options: MatchOptions = { phone: false },
): Record<string, SenderMatch> {
  const out: Record<string, SenderMatch> = {};
  for (const sender of senders) {
    const name = fold(sender);
    const number = options.phone ? senderPhone(sender, options.region) : null;
    if (name === fold(me.display_name) || (number && me.phone && number === me.phone)) {
      out[sender] = { userId: me.id, reason: "you" };
      continue;
    }
    const byName = people.filter((p) => fold(p.display_name) === name);
    if (byName.length === 1) {
      out[sender] = { userId: byName[0]!.id, reason: "name" };
      continue;
    }
    const byPhone = number ? people.filter((p) => p.phone && p.phone === number) : [];
    if (byPhone.length === 1) {
      out[sender] = { userId: byPhone[0]!.id, reason: "phone" };
      continue;
    }
    const found = options.found?.[sender];
    if (found && (found.reason === "email" || options.phone)) {
      out[sender] = found;
      continue;
    }
    out[sender] = { userId: null, reason: null };
  }
  return out;
}

/**
 * What to import into when nothing is chosen yet: a direct chat when the export has one other
 * person and they are matched, a group otherwise.
 */
export function suggestTarget(
  senders: string[],
  matches: Record<string, SenderMatch>,
  meId: string,
): { kind: "direct"; userId: string } | { kind: "group"; memberIds: string[] } {
  const others = [
    ...new Set(
      senders.map((s) => matches[s]?.userId).filter((id): id is string => Boolean(id && id !== meId)),
    ),
  ];
  const everyoneMatched = senders.every((s) => matches[s]?.userId);
  if (others.length === 1 && senders.length <= 2 && everyoneMatched)
    return { kind: "direct", userId: others[0]! };
  return { kind: "group", memberIds: others };
}

/** The first 16 bytes of a SHA-256, as URL-safe base64. */
async function shortHash(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return toBase64(new Uint8Array(digest).subarray(0, 16))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * A stable id for each message. It comes from the message itself, its time, sender, text and file,
 * plus a count for exact repeats, rather than from its line. So importing the same export again,
 * or a later export of the same chat, adds only what is new.
 */
export async function messageIds(messages: WhatsAppMessage[]): Promise<string[]> {
  const seen = new Map<string, number>();
  const ids: string[] = [];
  for (const m of messages) {
    const key = [
      m.time,
      m.sender ?? "",
      m.body,
      m.attachment ?? "",
      m.omitted ?? "",
      m.system ? "s" : "",
    ].join("\u0000");
    const n = seen.get(key) ?? 0;
    seen.set(key, n + 1);
    ids.push(await shortHash(`whatsapp\u0000${key}\u0000${n}`));
  }
  return ids;
}

/** What one parsed message becomes, before any media is uploaded. */
export function toImported(
  m: WhatsAppMessage,
  id: string,
  matches: Record<string, SenderMatch>,
): ImportedMessage {
  const out: ImportedMessage = { id, original_sender: m.system ? null : m.sender, original_time: m.time };
  const userId = m.sender ? matches[m.sender]?.userId : null;
  if (userId && !m.system) out.sender_id = userId;
  if (m.body) out.body = m.body;
  if (m.omitted) out.omitted = m.omitted;
  if (m.deleted) out.deleted = true;
  if (m.edited) out.edited = true;
  if (m.system) out.system = true;
  return out;
}

/** The kind of Rhema media message a file from the export becomes. */
export function mediaKindOf(name: string, mime: string): ImportedMessage["media_kind"] {
  if (/-PTT-|^PTT-|\.opus$/i.test(name)) return "voice";
  if (mime.startsWith("image/") && !/\.webp$/i.test(name)) return "photo";
  if (mime.startsWith("video/")) return "video";
  return "file";
}
